import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface LegalDocument {
  id: string;
  title: string;
  document_type: string;
  version: string;
  status: 'draft' | 'review' | 'published' | 'archived';
  content?: string;
  pdf_url?: string;
  file_path?: string;
  file_size?: number;
  effective_date?: string;
  expiry_date?: string;
  last_reviewed_at?: string;
  reviewed_by?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  is_active: boolean;
  metadata?: any;
}

export const useLegalDocuments = () => {
  return useQuery({
    queryKey: ['legal-documents'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('legal_documents' as any)
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return (data as any) as LegalDocument[];
    },
  });
};

export const usePublicLegalDocument = (documentType: string, options?: any) => {
  return useQuery({
    queryKey: ['legal-document', documentType],
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc('get_active_legal_document' as any, { doc_type: documentType });
      
      if (error) throw error;
      return data?.[0] || null;
    },
    enabled: !!documentType,
    ...options,
  });
};

export const useCreateLegalDocument = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (document: Partial<LegalDocument>) => {
      const { data, error } = await supabase
        .from('legal_documents' as any)
        .insert(document)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['legal-documents'] });
      toast.success('Legal document created successfully');
    },
    onError: (error: any) => {
      toast.error('Failed to create legal document: ' + error.message);
    },
  });
};

export const useUpdateLegalDocument = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<LegalDocument> & { id: string }) => {
      const { data, error } = await supabase
        .from('legal_documents' as any)
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['legal-documents'] });
      queryClient.invalidateQueries({ queryKey: ['legal-document', data.document_type] });
      toast.success('Legal document updated successfully');
    },
    onError: (error: any) => {
      toast.error('Failed to update legal document: ' + error.message);
    },
  });
};

export const useDeleteLegalDocument = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('legal_documents' as any)
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['legal-documents'] });
      toast.success('Legal document deleted successfully');
    },
    onError: (error: any) => {
      toast.error('Failed to delete legal document: ' + error.message);
    },
  });
};

export const useUploadLegalDocumentPDF = () => {
  return useMutation({
    mutationFn: async ({ file, path }: { file: File; path: string }) => {
      const { data, error } = await supabase.storage
        .from('legal-documents')
        .upload(path, file, {
          cacheControl: '3600',
          upsert: true,
        });
      
      if (error) throw error;
      
      const { data: urlData } = supabase.storage
        .from('legal-documents')
        .getPublicUrl(path);
      
      return {
        path: data.path,
        url: urlData.publicUrl,
        size: file.size,
      };
    },
    onError: (error: any) => {
      toast.error('Failed to upload PDF: ' + error.message);
    },
  });
};
